import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FiadBadge from "@/components/FiadBadge";
import Logo from "@/components/Logo";
import { LogOut, Loader2, ArrowRight, Clock, Info, CheckCircle2, XCircle, Trash2, UploadCloud } from "lucide-react";

const STATUS_UI = {
  under_review: { icon: Clock, color: "#026AA2", title: "Under review", msg: "Your application has been received and is currently being reviewed." },
  resubmitted: { icon: Clock, color: "#026AA2", title: "Under review", msg: "Your updated application has been received and is being reviewed." },
  more_info: { icon: Info, color: "#B54708", title: "More information required", msg: "Additional information is required before your application can be reviewed further." },
  approved: { icon: CheckCircle2, color: "#059669", title: "Approved", msg: "Your application has been approved. You can now begin the relevant evaluation." },
  evaluation_in_progress: { icon: Loader2, color: "#026AA2", title: "Evaluation in progress", msg: "Your evaluation is currently in progress." },
  evaluation_completed: { icon: CheckCircle2, color: "#059669", title: "Evaluation completed", msg: "Your evaluation has been submitted." },
  not_selected: { icon: XCircle, color: "#525252", title: "Not selected for the current pathway", msg: "Your profile is not being progressed for the current pathway at this time. This does not prevent you from being considered for future FIAD cohorts or opportunities." },
};

function Header({ email, onLogout }) {
  return (
    <header className="w-full border-b border-[#E5E5E5] bg-white">
      <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2"><Logo variant="dark" to={null} /></div>
        <div className="flex items-center gap-4"><span className="text-sm text-[#525252] hidden sm:inline" data-testid="user-email">{email}</span><button data-testid="logout-btn" onClick={onLogout} className="flex items-center gap-1.5 text-sm font-medium text-[#525252] hover:text-[#0A0A0A]"><LogOut className="h-4 w-4" /> Sign out</button></div>
      </div>
    </header>
  );
}

function ResubmitForm({ app, onDone }) {
  const [experience, setExperience] = useState(app.bengali_experience || "");
  const [message, setMessage] = useState("");
  const [resume, setResume] = useState(null);
  const [supporting, setSupporting] = useState([]);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const changedExp = experience.trim() && experience.trim() !== (app.bengali_experience || "");
    if (!changedExp && !message.trim() && !resume && supporting.length === 0) { toast.error("Please provide the requested information before resubmitting."); return; }
    const fd = new FormData();
    fd.append("message", message);
    fd.append("bengali_experience", experience);
    if (resume) fd.append("resume", resume);
    supporting.forEach((f) => fd.append("supporting_documents", f));
    setSaving(true);
    try { await api.post("/application/resubmit", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Resubmitted for review."); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not resubmit."); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-4 border-t border-[#E5E5E5] pt-4 space-y-4" data-testid="resubmit-form">
      <div><label className="block text-sm font-medium mb-1.5">Update your Bengali experience</label><textarea data-testid="resub-experience" rows={4} value={experience} onChange={(e) => setExperience(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black" /></div>
      <div><label className="block text-sm font-medium mb-1.5">Message to reviewer (optional)</label><textarea data-testid="resub-message" rows={2} value={message} onChange={(e) => setMessage(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black" /></div>
      <div><label className="block text-sm font-medium mb-1.5">Replace resume (optional)</label><input data-testid="resub-resume" type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResume(e.target.files?.[0] || null)} className="text-sm" /></div>
      <div><label className="block text-sm font-medium mb-1.5">Add supporting documents (optional)</label><input data-testid="resub-supporting" type="file" multiple onChange={(e) => setSupporting(Array.from(e.target.files || []))} className="text-sm" /></div>
      <button data-testid="resubmit-btn" onClick={submit} disabled={saving} className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#262626] disabled:opacity-60"><UploadCloud className="h-4 w-4" /> {saving ? "Resubmitting…" : "Resubmit application"}</button>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [app, setApp] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [booting, setBooting] = useState(true);

  const load = useCallback(async () => {
    try { const res = await api.get("/application"); setApp(res.data.application); setEvaluation(res.data.evaluation); }
    catch (e) { if (e?.response?.status === 401) { navigate("/login"); return; } }
    finally { setBooting(false); }
  }, [navigate]);

  useEffect(() => { if (loading) return; if (!user) { navigate("/login"); return; } load(); }, [loading, user, navigate, load]);

  if (loading || (user && booting)) return <div className="min-h-screen flex items-center justify-center bg-[#F6F3EC]"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user) return null;

  const status = app?.status;
  const ui = status ? STATUS_UI[status] : null;
  const Icon = ui?.icon;

  return (
    <div className="min-h-screen bg-[#F6F3EC]">
      <Header email={user.email} onLogout={async () => { await logout(); navigate("/"); }} />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-3xl font-bold tracking-tight">Your FIAD dashboard</h1>

        {!app && (
          <div className="mt-8 space-y-4" data-testid="no-application">
            <div className="bg-white border border-[#0A0A0A] rounded-sm p-6">
              <h2 className="font-display text-xl font-bold tracking-tight">Apply for the Bengali Linguist cohort</h2>
              <p className="mt-2 text-sm text-[#525252]">We're currently onboarding Bengali linguistic experts. Complete an application to be reviewed.</p>
              <button data-testid="start-application-btn" onClick={() => navigate("/apply")} className="mt-4 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#262626]">Complete Application <ArrowRight className="h-4 w-4" /></button>
            </div>
            <div className="bg-white border border-[#E5E5E5] rounded-sm p-6">
              <h2 className="font-display text-lg font-bold tracking-tight">Different domain?</h2>
              <p className="mt-2 text-sm text-[#525252]">Join the waitlist and we may contact you when a relevant cohort opens.</p>
              <Link to="/waitlist" data-testid="waitlist-link" className="mt-3 inline-block text-sm font-medium underline">Join the waitlist</Link>
            </div>
          </div>
        )}

        {app && ui && (
          <div className="mt-8">
            <div className="bg-white border rounded-sm p-6" style={{ borderColor: ui.color }} data-testid="application-status-card">
              <div className="inline-flex items-center gap-2" style={{ color: ui.color }}>
                <Icon className="h-5 w-5" strokeWidth={1.75} />
                <span data-testid="status-title" className="text-xs font-medium uppercase tracking-wider">{ui.title}</span>
              </div>
              <p className="mt-3 text-sm text-[#525252]" data-testid="status-message">{ui.msg}</p>

              {status === "more_info" && app.admin_message && (
                <div className="mt-4 bg-[#FFFAEB] border border-[#FEDF89] rounded-sm p-4 text-sm text-[#B54708]" data-testid="admin-message"><strong>Reviewer request:</strong> {app.admin_message}</div>
              )}
              {status === "more_info" && <ResubmitForm app={app} onDone={load} />}

              {(status === "approved" || status === "evaluation_in_progress") && (
                <button data-testid="begin-evaluation-btn" onClick={() => navigate("/path/bengali")} className="mt-5 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#262626]">{status === "approved" ? "Begin evaluation" : "Continue evaluation"} <ArrowRight className="h-4 w-4" /></button>
              )}

              {status === "evaluation_completed" && (
                <div className="mt-5" data-testid="evaluation-result">
                  {evaluation?.passed ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
                      <div className="sm:col-span-2">
                        <p className="text-sm text-[#059669] font-medium">FIAD Vetted Expert — Bengali Language Evaluation</p>
                        <p className="mt-1 text-sm text-[#525252]">Score: {evaluation.last_score}/100</p>
                      </div>
                      <FiadBadge testId="dashboard-badge" compact />
                    </div>
                  ) : (
                    <button data-testid="review-evaluation-btn" onClick={() => navigate("/path/bengali")} className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-2.5 rounded-sm text-sm font-medium">Review evaluation <ArrowRight className="h-4 w-4" /></button>
                  )}
                </div>
              )}
            </div>

            {/* What happens next */}
            {(status === "under_review" || status === "resubmitted") && (
              <div className="mt-6 bg-white border border-[#E5E5E5] rounded-sm p-6" data-testid="next-steps">
                <h3 className="font-display text-lg font-bold tracking-tight">What happens next?</h3>
                <ol className="mt-3 space-y-2 text-sm text-[#525252] list-decimal pl-5">
                  <li>Your profile will be reviewed for the current pathway.</li>
                  <li>We may request additional information if anything is missing or unclear.</li>
                  <li>If approved, your evaluation pathway will be unlocked and you will be notified.</li>
                </ol>
                <p className="mt-3 text-xs text-[#A3A3A3]">Submitting an application does not guarantee acceptance, FIAD Vetted status, a project, employment or income.</p>
              </div>
            )}
          </div>
        )}

        {/* Profile deletion request */}
        <div className="mt-10 bg-white border border-[#E5E5E5] rounded-sm p-6" data-testid="deletion-section">
          <h3 className="font-display text-lg font-bold tracking-tight">Want to delete your FIAD profile?</h3>
          <p className="mt-2 text-sm text-[#525252]">You can request deletion of your FIAD account and relevant personal information by contacting us at contact.fiad@gmail.com. Please send your request using the email address associated with your FIAD account so we can verify your request. Some information may need to be retained where required by law or reasonably necessary for security, fraud prevention, or legal purposes.</p>
          <a data-testid="request-deletion-btn" href="mailto:contact.fiad@gmail.com?subject=FIAD%20Profile%20Deletion%20Request" className="mt-4 inline-flex items-center gap-2 border border-[#0A0A0A] text-[#0A0A0A] px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#F5F5F5]"><Trash2 className="h-4 w-4" /> Request Profile Deletion</a>
        </div>
      </div>
    </div>
  );
}
